import React, { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { DocumentUploadCard } from "../ui/DocumentUploadCard";
import { UploadedFiles } from "../UploadedFiles";
import { InvalidRequirementReason } from "../InvalidRequirementReason";
import { CancelToReview } from "../CancelToReview";
import { Status } from "../../../redux/interfaces/folder.types";
import { toast } from "sonner";
import { uploadFileRequirement } from "../../../utils/vault/uploadFileRequirement";
import { useUploadFileToVault } from "../../../hooks/vault/mutations/useUploadFileToVault";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import type { UploadFile } from "antd/es/upload/interface";
import { POWER_OF_ATTORNEY_MAX_FILE_SIZE_MB } from "../../../utils/vault/verificationUploadLimits";

interface Props {
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
}

export const PowerOfAttorney: React.FC<Props> = ({ onRegisterSubmit }) => {
  const [files, setFiles] = useState<UploadFile[]>([]);

  const dispatch = useAppDispatch();

  const { activeRequirement, activeVault, activeTab } = useAppSelector(
    (state) => state.vault,
  );
  const uploadMutation = useUploadFileToVault(activeVault?.id);
  const updateFormValuesMutation = useUpdateVaultFormValues(activeVault?.id);

  const enabledInputs =
    activeRequirement?.status === Status.PendingUpload ||
    activeRequirement?.status === Status.Invalid;

  // Reset files when requirement changes
  useEffect(() => {
    setFiles([]);
  }, [activeRequirement?.id]);

  const handleSubmit = async (): Promise<boolean> => {
    if (files.length === 0) {
      toast.error("Por favor sube un archivo");
      return false;
    }

    const file = files[0]?.originFileObj;
    const formFiles = [file];
    const documentTypeKey = "power_of_attorney";
    const requirementFiles = activeRequirement?.files || [];
    const formNFiles = 1;
    const successMessage =
      "Poder del representante legal subido correctamente.";

    const messageApi = {
      success: (msg: string) => toast.success(msg),
      error: (msg: string) => toast.error(msg),
    };

    try {
      await uploadFileRequirement({
        uploadMutateAsync: uploadMutation.mutateAsync,
        updateFormValuesMutateAsync: updateFormValuesMutation.mutateAsync,
        dispatch,
        requirementFiles,
        documentType: documentTypeKey,
        formNFiles,
        formFiles,
        formValues: {},
        activeRequirement,
        activeVault,
        activeTab,
        successMessage,
        messageApi,
      });
      setFiles([]);
      return true;
      setFiles([]);
      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      return false;
    }
  };

  // Register submit function with parent for external triggering
  const submitFnRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  submitFnRef.current = handleSubmit;

  useEffect(() => {
    if (!onRegisterSubmit) return;
    const fn = async () => submitFnRef.current?.() ?? false;
    onRegisterSubmit(fn);
    return () => onRegisterSubmit(null);
  }, [onRegisterSubmit]);

  const tips = [
    "Documento legible y completo",
    "Todas las páginas deben ser visibles",
    "Debe indicar las facultades del representante",
    "Un solo archivo PDF con todas las páginas",
  ];

  return (
    <div className="w-full">
      {/* Description */}
      <p className="text-gray-600 mb-8">
        Adjuntar el poder del{" "}
        <span className="font-semibold text-gray-900">representante legal</span>{" "}
        en donde se indiquen las facultades del mismo dentro de la empresa.
      </p>

      {/* Invalid Requirement Reason — shown at the top so the rejection
          message is visible before the user scrolls through the form. */}
      <InvalidRequirementReason requirement={activeRequirement!} />

      {/* Two column layout - stack on mobile, 40/60 split on md+ */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 md:gap-8">
        {/* Left Column - Tips */}
        <div className="space-y-6 md:sticky md:top-0 md:self-start">
          {/* Tips */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Requisitos
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              {tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>

            {/* Note about acta constitutiva */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <span className="font-medium">Nota:</span> En caso de que el
                poder del representante legal se encuentre en el acta
                constitutiva, adjuntarla de nuevo aquí.
              </p>
            </div>
          </div>
        </div>

        {/* Right Column - File Upload */}
        <div className="space-y-6">
          <div className="w-full">
            {/* Label */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Poder legal
              </span>
            </div>
            <DocumentUploadCard
              name="power_of_attorney"
              label="Poder del representante legal"
              type="both"
              enableCropper={false}
              maxSizeMB={POWER_OF_ATTORNEY_MAX_FILE_SIZE_MB}
              onFileChange={setFiles}
              value={files}
            />
          </div>

          {/* Uploaded Files */}
          <UploadedFiles />

          {/* Cancel to review (when already submitted) */}
          {!enabledInputs &&
            activeRequirement?.status === Status.PendingReview && (
              <CancelToReview requirement={activeRequirement} />
            )}
        </div>
      </div>
    </div>
  );
};
